<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Show" :items="['bottles','cases','LAA','litres','value']" outlined dense hide-details v-model="show"/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Type" :items="[{text:'All',value:null},{divider:true},{text:'In-bond',value:true},{text:'Duty-paid',value:false}]" outlined dense hide-details v-model="inBond" @change="refresh()"/>
        </v-col>
        <v-col cols="4">
          <inventory-categories-select v-model="categoryId" label="Category" type="Product" none-text="All" @change="refresh()" hide-details/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Product period summary', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row dense class="pb-0">
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 228px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import {add,format} from 'date-fns';
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'

export default {
  name: 'ReportsProductsPeriodSummary',
  mixins: [printHtml],
  components: {InventoryCategoriesSelect},
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      siteId: null,
      show: 'bottles',
      inBond: true,
      categoryId: null,
      loading: true,
      products: [],
      totals: {start:{},end:{}}
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    }
  },
  async created() {
    this.$emit('name', 'Products period summary')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
    this.inBond = !this.$route.query.dutyPaid;
  },
  methods: {
    async refresh() {
      this.loading = true;
      const start = [];
      const end = [];
      const filters = [{field:'active', comparator:'eq', value:'true'}];
      if (this.categoryId)
        filters.push({field:'categoryId', comparator:'in', value:this.categoryId});
      await Promise.all([
        this.$lazyListLoad(start, 'products2',
                          'id,name,bottleSize,caseSize,strength,sites {siteId,inBondBottles,inBondValue,dutyPaidBottles,dutyPaidValue}', 100,
                          filters,[{name:'asof', value:format(add(new Date(this.from+'T23:59:59'), {days:-1}),'yyyy-MM-dd HH:mm:ss')}]),
        this.$lazyListLoad(end, 'products2',
                          'id,sites {siteId,inBondBottles,inBondValue,dutyPaidBottles,dutyPaidValue}', 100,
                          filters,[{name:'asof',value:`${this.to} 23:59:59`}])
      ]);

      start.forEach(product => product.totalsStart = this.buildTotals(product.sites, product));
      this.products = start;
      end.forEach(endProduct => {
        const product = this.products.find(product => product.id === endProduct.id);
        product.totalsEnd = this.buildTotals(endProduct.sites, product);
      });

      this.totals = this.products.reduce(({start,end}, product) => (
        {start:{bottles:start.bottles + product.totalsStart.bottles,
                        cases:start.cases + product.totalsStart.cases,
                        LAA:start.LAA + product.totalsStart.LAA,
                        litres:start.litres + product.totalsStart.litres,
                        value:start.value + product.totalsStart.value},
                end: {bottles:end.bottles + product.totalsEnd.bottles,
                      cases:end.cases + product.totalsEnd.cases,
                      LAA:end.LAA + product.totalsEnd.LAA,
                      litres:end.litres + product.totalsEnd.litres,
                      value:end.value + product.totalsEnd.value}
        }
      ), {start:{bottles:0,cases:0,LAA:0,litres:0,value:0},end:{bottles:0,cases:0,LAA:0,litres:0,value:0}});
      this.loading = false;
    },

    buildTotals(sites, product) {
      return sites.reduce(({bottles,cases,LAA,litres,value},site) => {
        if (!this.siteId || this.siteId === site.siteId) {
          const totals = {bottles: bottles + (this.inBond ? site.inBondBottles : site.dutyPaidBottles),
                          value: value + (this.inBond ? site.inBondValue : site.dutyPaidValue)};
          totals.cases = this.$roundNumber(totals.bottles / product.caseSize, 1);
          totals.LAA = this.$roundNumber(totals.bottles * product.bottleSize/1000 * product.strength/100, 3);
          totals.litres = this.$roundNumber(totals.bottles * product.bottleSize/1000, 3);
          return totals;
        }
        return {bottles,cases,LAA,litres,value};
      }, {bottles:0,cases:0,LAA:0,litres:0,value:0});
    },

    report() {
      const lines = [
                  `<style>
                    @page {size:portrait}
                    .reports-products-summary {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .reports-products-summary th {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-products-summary .spacer {height:18px}
                    .reports-products-summary td {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-products-summary .title {font-size:18px;text-transform:uppercase}
                    .reports-products-summary .sup-title {font-weight:normal;font-size:22px}
                    .reports-products-summary .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .reports-products-summary .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .reports-products-summary .text-left {text-align:left}
                    .reports-products-summary .text-right {text-align:right}
                    .reports-products-summary .font-weight-bold {font-weight:bold}
                    .reports-products-summary .total-row {font-weight:bold}
                    .reports-products-summary .capitalize {text-transform: capitalize}
                  </style>
                   <table class="reports-products-summary">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">Products period summary</th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>

                        <tr class="header">
                          <th width="40%" class="text-left">Product</th>
                          <th width="20%" class="text-right">Start ${this.show}</th>
                          <th width="20%" class="text-right">Change ${this.show}</th>
                          <th width="20%" class="text-right">End ${this.show}</th>
                        </tr>
                      </thead>
                      <tbody>`
      ];
      for (const product of this.products) {
        lines.push(`<tr>`);
        lines.push(`<td>${product.name}</td>`);
        lines.push(`<td class="text-right">${this.number(product.totalsStart[this.show])}</td>`);
        lines.push(`<td class="text-right">${this.number(product.totalsEnd[this.show]-product.totalsStart[this.show])}</td>`);
        lines.push(`<td class="text-right">${this.number(product.totalsEnd[this.show])}</td>`);
      }
      lines.push(`<tr :class="dark ? 'total-row-dark' : 'total-row'">
                    <td class="text-right">Total</td>
                    <td class="text-right">${this.number(this.totals.start[this.show])}</td>
                    <td class="text-right">${this.number(this.totals.end[this.show]-this.totals.start[this.show])}</td>
                    <td class="text-right">${this.number(this.totals.end[this.show])}</td>
                  </tr>`);
      lines.push(`</tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },
    number(num) {
      if (this.show === 'value')
        return `$${this.$number(num, 2)}`;
      return this.$number(num, this.show === 'bottles' ? 0 : (this.show === 'cases' ? 1 : 3));
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,`,
                      `"Products period summary",,,`,
                      `"${this.dateDisplay}",,,`,
                      `"Product","Start ${this.show}","Change ${this.show}","End ${this.show}"`
                    ];
      for (const product of this.products)
        lines.push(`"${product.name}","${this.number(product.totalsStart[this.show])}","${this.number(product.totalsEnd[this.show]-product.totalsStart[this.show])}","${this.number(product.totalsEnd[this.show])}"`);
      lines.push(`"Total","${this.number(this.totals.start[this.show])}","${this.number(this.totals.end[this.show]-this.totals.start[this.show])}","${this.number(this.totals.end[this.show])}"`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Products period summary.csv');
    }
  }
}
</script>